/**
 * AssignQuestionnairesFlow
 * ========================
 *
 * Single-screen flow for assigning an additional life stage questionnaire
 * to an existing informant (e.g. adding the Childhood questionnaire to an
 * informant who currently only has Adulthood).
 *
 * Shows a before/after recap of the change and warns if another informant
 * will be displaced from the target life stage.  On success, shows a
 * confirmation screen.
 *
 * Owns the success state internally so the parent doesn't need to track
 * assignmentSuccess or the displaced informant computation.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import BeforeAfterRecap from './BeforeAfterRecap';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';

/**
 * @param {Object}   props
 * @param {Object}   props.informant         – The informant getting a new stage
 * @param {Array}    props.allInformants      – All informants (to find displaced informant)
 * @param {string}   props.targetLifeStage    – 'Adulthood' or 'Childhood'
 * @param {Function} props.onAssign           – (informantId, lifeStage) => Promise
 * @param {Function} props.onBack             – Navigate back to the detail screen
 * @param {boolean}  props.actionLoading      – Whether the backend call is in progress
 * @param {string}   props.actionError        – Error message from the backend
 * @param {Function} props.clearActionState   – Reset actionError/actionSuccess
 * @param {Function} props.onFinish           – Called when user clicks "Finish" on success screen
 * @param {boolean}  props.isFinishing        – Show spinner on the "Finish" button
 * @param {Object}   props.terminology        – { singular, lowerSingular }
 */
export default function AssignQuestionnairesFlow({
  informant,
  allInformants = [],
  targetLifeStage,
  onAssign,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
}) {
  const [isSuccess, setIsSuccess] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Find any informant already covering the target life stage that would
  // be displaced by this assignment.
  const displacedInformant = useMemo(() => {
    return allInformants.find(
      inf => inf.id !== informant?.id && (inf.lifeStages || []).includes(targetLifeStage)
    );
  }, [allInformants, informant, targetLifeStage]);

  // Build before/after data for the recap.
  const currentStages = informant?.lifeStages || [];
  const newStages = [...currentStages, targetLifeStage];

  const beforeEntries = allInformants.map(inf => ({
    id: inf.id,
    name: `${inf.firstName} ${inf.lastName}`,
    lifeStages: inf.lifeStages || [],
  }));

  const afterEntries = allInformants
    .map(inf => {
      if (inf.id === informant?.id) {
        return { id: inf.id, name: `${inf.firstName} ${inf.lastName}`, lifeStages: newStages };
      }
      if ((inf.lifeStages || []).includes(targetLifeStage)) {
        const remaining = (inf.lifeStages || []).filter(s => s !== targetLifeStage);
        if (remaining.length === 0) return null;
        return { id: inf.id, name: `${inf.firstName} ${inf.lastName}`, lifeStages: remaining };
      }
      return { id: inf.id, name: `${inf.firstName} ${inf.lastName}`, lifeStages: inf.lifeStages || [] };
    })
    .filter(Boolean);

  const handleConfirm = useCallback(async () => {
    try {
      await onAssign(informant.id, targetLifeStage);
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook
    }
  }, [onAssign, informant, targetLifeStage]);

  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline="Questionnaire assigned successfully!"
        detail={`${informant.firstName} ${informant.lastName} is now assigned to the ${targetLifeStage?.toLowerCase()} questionnaire.`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Assign {targetLifeStage} Questionnaire
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Assign the {targetLifeStage?.toLowerCase()} questionnaire to{' '}
          <span className="font-medium">
            {informant.firstName} {informant.lastName}
          </span>
          .
        </p>
      </div>

      <BeforeAfterRecap
        before={beforeEntries}
        after={afterEntries}
        operationLabel="Reassign Questionnaire"
      />

      {displacedInformant && (
        <WarningPanel variant="warning">
          <p className="font-semibold mb-1">
            {displacedInformant.firstName} {displacedInformant.lastName} will be
            affected
          </p>
          <p>
            They are currently assigned to the {targetLifeStage?.toLowerCase()}{' '}
            questionnaire.
            {(displacedInformant.lifeStages || []).length === 1
              ? ` They will be removed as an ${terminology.lowerSingular} since they would have no remaining questionnaires.`
              : ' They will keep their other questionnaire assignment.'}
          </p>
          {displacedInformant.hasStarted && (
            <p className="mt-1 font-semibold">
              Their existing progress will be archived.
            </p>
          )}
        </WarningPanel>
      )}

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => { clearActionState?.(); onBack(); }}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={actionLoading}
          className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          {actionLoading ? (
            <span className="inline-flex items-center gap-2">
              <ButtonSpinner />
              Assigning…
            </span>
          ) : 'Confirm Assignment'}
        </button>
      </div>
    </div>
  );
}
